'use client';

import { GrowthBookContext } from '@growthbook/growthbook-react';
import { DeepMaybe, isBrowser } from '@sortlist-frontend/utils';
import { useContext } from 'react';

import { config } from '../defaults';
import { useFeatureFlagConfigOverride } from './FeatureFlagsProvider';
import {
  AvailableFeatureNames,
  AvailableFeatureToggles,
  Experiment,
  FeatureToggleConfig,
  isFeatureFlagWithExperiment,
  isFeatureFlagWithVariants,
} from './types';
import { getValueFromEvaluatedFeature, isPathnameActivatedForExperiment, isValueActive } from './utils';

export function useIsFeatureActive<
  T extends AvailableFeatureNames,
  F extends AvailableFeatureToggles[T],
  V extends DeepMaybe<F, ['variants', number]>,
  E extends DeepMaybe<F, ['experiment']> & Experiment,
>(
  featureName: T,
): {
  /**
   * boolean: true or false if the feature is enabled in any variant excluding control
   */
  isActive: boolean;
  /**
   * string: returns the currently activated variant including control
   */
  variant?: V;
  /**
   * string: returns the currently given flag, provided for type safety on the consumer side
   */
  flag: T;
  /**
   * string: currently active experiment if it is running. The value is extracted from the config
   */
  experiment?: E;
} {
  const configOverride = useFeatureFlagConfigOverride();
  const { growthbook } = useContext(GrowthBookContext);
  const currentConfig = config[featureName];

  const overrideValueFromEnvironment = configOverride?.configOverrideFromEnvironment[featureName]?.defaultValue;

  const overriddenValue =
    configOverride?.configOverrideFromParams[featureName]?.defaultValue ??
    configOverride?.configOverrideFromLocalStorage[featureName]?.defaultValue;
  const isFeatureOverridden = overriddenValue != null || overrideValueFromEnvironment != null;

  // If the feature is overridden by other parameters, return early
  if (isFeatureOverridden) {
    const resolvedValue = overriddenValue ?? overrideValueFromEnvironment ?? false;
    const isActive = isValueActive(resolvedValue);

    return {
      isActive,
      variant:
        isFeatureFlagWithVariants(currentConfig) && typeof resolvedValue === 'string'
          ? (resolvedValue as unknown as V)
          : undefined,
      experiment: undefined,
      flag: featureName,
    };
  }

  if (!isFeatureFlagWithExperiment(currentConfig)) {
    return {
      isActive: isValueActive(currentConfig.defaultValue),
      variant: getVariant(currentConfig) as V,
      experiment: undefined,
      flag: featureName,
    };
  }

  if (
    isFeatureFlagWithExperiment(currentConfig) &&
    isBrowser() &&
    !isPathnameActivatedForExperiment(currentConfig.experiment, window.location.href)
  ) {
    return {
      isActive: false,
      variant: undefined,
      experiment: undefined,
      flag: featureName,
    };
  }

  const feature = growthbook.evalFeature<T>(featureName);
  const valueFromExperiment = getValueFromEvaluatedFeature<T>(feature);

  const value = valueFromExperiment ?? overrideValueFromEnvironment ?? currentConfig.defaultValue;

  const isActive = isValueActive(value);
  const experimentConfig = getExperiment(currentConfig);

  return {
    isActive,
    variant: value as unknown as V,
    experiment: experimentConfig as E,
    flag: featureName,
  };
}

function getExperiment(config: FeatureToggleConfig) {
  if (isFeatureFlagWithExperiment(config)) {
    return config.experiment;
  }
}

function getVariant(config: FeatureToggleConfig) {
  const value = config.defaultValue;
  if (typeof value === 'string' && isFeatureFlagWithVariants(config)) {
    return value as keyof typeof config.variants;
  }
}
